import {
    AcceptToggle,
    PoolDiscardChangesModal,
    PoolFormattedMessage,
    PoolFormikNumberTextfield,
    PoolLineClampTypography,
    PoolLink,
    PoolRow,
    PoolTooltip,
    PoolVirtualTable,
} from '@synergy/components-next';
import { AddUserIcon, AlertCallSignIcon, CommentNoteIcon } from '@synergy/theme-next';
import { BonusCustomerCondition, CustomerBonusSettlement } from '@synergy/models';
import { Box, Paper, Stack, TableCell, TableRow, Tooltip } from '@mui/material';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Fragment, useEffect, useState } from 'react';
import { InformationOutlinedIcon, theme } from '@synergy/theme';
import { getConditionCalculatedBonus, getConditionMemberBonus, getCurrencySymbol } from '@synergy/utils';
import { useFormikContext } from 'formik';
import { useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import match from 'autosuggest-highlight/match';

import { BonusConditionTableCellFooter } from './components/bonus-condition-table-cell-footer';
import { BonusConditionTableCellHeader } from './components/bonus-condition-table-cell-header';
import { BonusConditionTableCellValue } from './components/bonus-condition-table-cell-value';
import { BonusSettlementSupplierDetailEditModal } from './components/bonus-settlement-supplier-detail-edit-modal';
import { BonusSettlementSupplierDetailFormValues } from './models/bonus-settlement-supplier-detail-form-values';
import { BonusSettlementSupplierDetailTableActionMenu } from './bonus-settlement-supplier-detail-table-action-menu';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { bonusSettlementsSupplierDetailSelector } from './store/bonus-settlements-supplier-detail-selector';
import { defaultCurrencySelector } from '../../../common/selectors/currency-selectors';
import { reclaim, revertReclaim } from './store/bonus-settlements-supplier-detail-actions';
import { removeCustomer } from './store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export const BonusSettlementSupplierDetailTable = () => {
    const bonusSettlementSupplierDetail = useAppSelector(bonusSettlementsSupplierDetailSelector);
    const formikContext = useFormikContext<BonusSettlementSupplierDetailFormValues>();
    const defaultCurrency = useAppSelector(defaultCurrencySelector);
    const locale = useAppSelector(bestLocaleSelector);
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const [searchParams] = useSearchParams();
    const [openEditModal, setOpenEditModal] = useState(false);

    const [editModalData, setEditModalData] = useState<{
        index: number;
        settlement: CustomerBonusSettlement;
    } | null>(null);
    const [filteredSettlements, setFilteredSettlements] = useState<CustomerBonusSettlement[]>([]);

    useEffect(() => {
        const isFinished = searchParams.get('isFinished');
        const hasNoteToCustomer = searchParams.get('hasNoteToCustomer');
        const search = searchParams.get('search') || '';

        if (!isFinished && !search && !hasNoteToCustomer) {
            setFilteredSettlements(formikContext.values.customerBonusSettlements);
            return;
        }

        const filtered = formikContext.values.customerBonusSettlements.filter(settlement => {
            if (search) {
                const nameMatches = match(settlement.customerName, search, {
                    requireMatchAll: true,
                    insideWords: true,
                });
                const idMatches = match(settlement.customerId.toString(), search);
                if (nameMatches.length === 0 && idMatches.length === 0) {
                    return false;
                }
            }

            if (hasNoteToCustomer !== null) {
                const noteToCustomerLength = settlement?.noteToCustomer?.length ?? 0;
                const hasNote = noteToCustomerLength > 0;

                if (hasNoteToCustomer === 'true' && !hasNote) {
                    return false;
                }
                if (hasNoteToCustomer === 'false' && hasNote) {
                    return false;
                }
            }

            if (isFinished !== null) {
                if (isFinished === 'true' && !settlement.isFinished) {
                    return false;
                }
                if (isFinished === 'false' && settlement.isFinished) {
                    return false;
                }
            }

            return true;
        });

        setFilteredSettlements(filtered);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, formikContext.values.customerBonusSettlements]);

    const handleRecalculateOnSingleActualBonusChange = (value: string | null, index: number) => {
        const parsedValue = value ? parseFloat(value) : 0;
        const memberBonus = getConditionMemberBonus(
            parsedValue,
            formikContext.values.customerBonusSettlements[index],
            bonusConditions?.[0].bonusCondition || undefined
        );

        formikContext.setFieldValue(`customerBonusSettlements.${index}.actualBonus`, parsedValue);
        formikContext.setFieldValue(`customerBonusSettlements.${index}.memberBonus`, memberBonus);
    };

    const getSupplierPayouts = (): number[] => {
        return bonusConditions
            .map((condition: BonusCustomerCondition) => condition.bonusCondition.supplierPayout)
            .filter(
                (supplierPayout: number | null | undefined) => supplierPayout !== undefined && supplierPayout !== null
            ) as number[];
    };

    const handleRecalculate = (value: string | null, conditionId: number, index: number) => {
        let totalActualBonus = 0;
        let totalMemberBonus = 0;
        bonusSettlementSupplierDetail?.bonusConditions.forEach(condition => {
            const bonusConditionValueIndex = formikContext.values.customerBonusSettlements[
                index
            ]?.bonusConditionValues?.findIndex(bcv => bcv.bonusConditionId === condition.bonusCondition.id);

            let actualBonus = 0;
            if (condition.bonusCondition.id === conditionId) {
                actualBonus = getConditionCalculatedBonus(
                    value,
                    condition.bonusCondition.id,
                    bonusSettlementSupplierDetail?.bonusConditions ?? []
                );

                formikContext.setFieldValue(
                    `customerBonusSettlements.${index}.bonusConditionValues.${bonusConditionValueIndex}.actualBonus`,
                    actualBonus
                );
                formikContext.setFieldValue(
                    `customerBonusSettlements.${index}.bonusConditionValues.${bonusConditionValueIndex}.value`,
                    value
                );
            } else {
                actualBonus =
                    formikContext.values.customerBonusSettlements[index].bonusConditionValues[bonusConditionValueIndex]
                        ?.actualBonus ?? 0;
            }

            totalActualBonus += actualBonus;
            totalMemberBonus += getConditionMemberBonus(
                actualBonus,
                formikContext.values.customerBonusSettlements[index],
                condition.bonusCondition
            );
        });

        formikContext.setFieldValue(`customerBonusSettlements.${index}.calculatedBonus`, totalActualBonus);
        formikContext.setFieldValue(`customerBonusSettlements.${index}.actualBonus`, totalActualBonus);
        formikContext.setFieldValue(`customerBonusSettlements.${index}.memberBonus`, totalMemberBonus);
    };

    if (!bonusSettlementSupplierDetail) return null;

    const { bonusConditions } = bonusSettlementSupplierDetail;

    const handleEditCustomer = (index: number, settlement: CustomerBonusSettlement) => {
        setEditModalData({ index: index, settlement: settlement });
        setOpenEditModal(true);
    };

    const handleRemoveCustomer = (settlement: CustomerBonusSettlement) => {
        if (!bonusSettlementSupplierDetail.bonusSettlementId) {
            return;
        }

        dispatch(
            removeCustomer({
                bonusSettlementId: bonusSettlementSupplierDetail.bonusSettlementId,
                supplierBonusSettlementId: bonusSettlementSupplierDetail.id,
                customerId: settlement.id,
            })
        );
    };

    const handleReclaimCustomer = async (settlement: CustomerBonusSettlement) => {
        if (!bonusSettlementSupplierDetail.bonusSettlementId) {
            return;
        }

        const result = await dispatch(
            reclaim({
                bonusSettlementId: bonusSettlementSupplierDetail.bonusSettlementId,
                supplierBonusSettlementId: bonusSettlementSupplierDetail.id,
                customerBonusSettlementId: settlement.id,
            })
        );

        setReclaimForCustomer(result.meta.requestStatus, settlement.id, true);
    };

    const handleRevertReclaimCustomer = async (settlement: CustomerBonusSettlement) => {
        if (!bonusSettlementSupplierDetail.bonusSettlementId) {
            return;
        }

        const result = await dispatch(
            revertReclaim({
                bonusSettlementId: bonusSettlementSupplierDetail.bonusSettlementId,
                supplierBonusSettlementId: bonusSettlementSupplierDetail.id,
                customerBonusSettlementId: settlement.id,
            })
        );

        setReclaimForCustomer(result.meta.requestStatus, settlement.id, false);
    };

    const setReclaimForCustomer = (
        requestStatus: 'fulfilled' | 'rejected',
        customerBonusSettlementId: number,
        isReclaimed: boolean
    ) => {
        if (requestStatus === 'rejected') {
            return;
        }

        const index = formikContext.values.customerBonusSettlements.findIndex(s => s.id === customerBonusSettlementId);
        if (index === -1) {
            return;
        }

        formikContext.setFieldValue(`customerBonusSettlements.${index}.isReclaimed`, isReclaimed);
    };

    return (
        <Paper elevation={0} sx={{ height: '100%' }}>
            <PoolDiscardChangesModal isDirty={formikContext.dirty} />
            <PoolVirtualTable
                data={filteredSettlements}
                fixedHeaderContent={() => (
                    <TableRow sx={{ background: theme => theme.palette.common.white }}>
                        <TableCell sx={{ width: 400 }}>
                            <FormattedMessage id="app.table.column.customerWitIdAndLocation" />
                        </TableCell>
                        {bonusConditions.map((condition: BonusCustomerCondition) => {
                            return (
                                <BonusConditionTableCellHeader
                                    key={condition.bonusCondition.id}
                                    bonusCondition={condition.bonusCondition}
                                    defaultCurrency={defaultCurrency}
                                    locale={locale}
                                />
                            );
                        })}
                        <TableCell sx={{ width: 130, textAlign: 'right' }}>
                            <FormattedMessage id="app.table.column.bonusCalculated" />
                        </TableCell>
                        <TableCell sx={{ width: 140, textAlign: 'right' }}>
                            <FormattedMessage id="app.table.column.bonusActual" />
                        </TableCell>
                        <TableCell sx={{ width: 140, textAlign: 'right' }}>
                            <FormattedMessage id="app.table.column.bonusMember" />
                        </TableCell>
                        <TableCell sx={{ width: 110, textAlign: 'center' }}>
                            <FormattedMessage id="app.label.column.done" />
                        </TableCell>
                        {!bonusSettlementSupplierDetail.readOnly && <TableCell sx={{ p: 0 }} />}
                    </TableRow>
                )}
                rowRenderer={customerBonusSettlement => {
                    const index = formikContext.values.customerBonusSettlements.findIndex(
                        settlement => settlement.id === customerBonusSettlement.id
                    );
                    const bonusSettlement = formikContext.values.customerBonusSettlements[index];

                    const getCalculationTooltipPayout = () => {
                        if (bonusSettlement?.customerBonusPayout !== undefined) {
                            return intl.formatNumber(bonusSettlement.customerBonusPayout, { maximumFractionDigits: 2 });
                        }

                        const supplierPayouts = getSupplierPayouts();

                        if (supplierPayouts.length > 0) {
                            if (supplierPayouts.length === 1) {
                                return `${intl.formatNumber(supplierPayouts[0], { maximumFractionDigits: 2 })}`;
                            }

                            // FIXME the tooltip doesn't support displaying multiple supplier payouts.
                            return undefined;
                        }

                        return intl.formatNumber(bonusSettlement?.customerBonusBase * bonusSettlement?.payout || 0, {
                            maximumFractionDigits: 2,
                        });
                    };

                    const calculationPayout = getCalculationTooltipPayout();

                    return (
                        <Fragment key={index}>
                            <TableCell>
                                <PoolRow gap={2}>
                                    {customerBonusSettlement?.isAddedManually && (
                                        <PoolTooltip
                                            title={
                                                <FormattedMessage id="app.bonusSettlements.supplierDetail.addCustomerManually" />
                                            }
                                        >
                                            <AddUserIcon color="primary" />
                                        </PoolTooltip>
                                    )}
                                    {customerBonusSettlement?.isReclaimed && (
                                        <Tooltip
                                            title={
                                                <FormattedMessage id="app.bonusSettlement.reclaim.customer.toolTip" />
                                            }
                                        >
                                            <Box>
                                                <AlertCallSignIcon color="error" />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <Stack>
                                        <PoolLink
                                            href={`/customers/${customerBonusSettlement.customerId}`}
                                            hoverColor={theme.palette.text.primary}
                                            target="_blank"
                                            sx={{ '&:hover': { textDecoration: 'underline' } }}
                                            /* Should not be reached with tabulator */
                                            tabIndex={-1}
                                        >
                                            <PoolLineClampTypography>
                                                {customerBonusSettlement.customerName}
                                            </PoolLineClampTypography>
                                        </PoolLink>
                                        <Box typography="body2" color="grey.800">
                                            {customerBonusSettlement.customerId}
                                            {customerBonusSettlement.city ? `・${customerBonusSettlement.city}` : ''}
                                        </Box>
                                    </Stack>
                                </PoolRow>
                            </TableCell>
                            {bonusConditions.map((condition: BonusCustomerCondition) => {
                                const bonusConditionValueIndex = bonusSettlement?.bonusConditionValues?.findIndex(
                                    bcv => bcv.bonusConditionId === condition.bonusCondition.id
                                );

                                const value =
                                    bonusSettlement?.bonusConditionValues[bonusConditionValueIndex]?.value ?? 0;
                                const revenue =
                                    bonusSettlement?.bonusConditionValues[bonusConditionValueIndex]?.revenue;

                                return (
                                    <BonusConditionTableCellValue
                                        key={condition.bonusCondition.id}
                                        rowIndex={index}
                                        bonusConditionValueIndex={bonusConditionValueIndex}
                                        readOnly={bonusSettlementSupplierDetail.readOnly}
                                        bonusCondition={condition.bonusCondition}
                                        defaultCurrency={defaultCurrency}
                                        handleChange={value =>
                                            handleRecalculate(value, condition.bonusCondition.id, index)
                                        }
                                        locale={locale}
                                        value={value}
                                        revenue={revenue}
                                    />
                                );
                            })}
                            <TableCell sx={{ width: 130, textAlign: 'right' }}>
                                <FormattedNumber
                                    value={bonusSettlement?.calculatedBonus}
                                    currency={defaultCurrency}
                                    style="currency"
                                    maximumFractionDigits={2}
                                />
                            </TableCell>
                            <TableCell sx={{ width: 140 }}>
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>
                                    {/* Edit single actual bonus */}
                                    {bonusSettlement?.canEditBonus ? (
                                        <PoolFormikNumberTextfield
                                            name={`customerBonusSettlements.${index}.bonusConditionValues.${0}.actualBonus`}
                                            textAlign="right"
                                            placeholder="0"
                                            suffix={` ${getCurrencySymbol(defaultCurrency, locale)}`}
                                            fixedDecimalScale={true}
                                            decimalScale={2}
                                            fullWidth={true}
                                            onValueChange={value =>
                                                handleRecalculateOnSingleActualBonusChange(value, index)
                                            }
                                        />
                                    ) : (
                                        <FormattedNumber
                                            value={formikContext.values.customerBonusSettlements[index]?.actualBonus}
                                            currency={defaultCurrency}
                                            style="currency"
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                        />
                                    )}
                                    {bonusSettlement?.noteToCustomer && (
                                        <PoolTooltip
                                            variant="light"
                                            title={
                                                <Stack>
                                                    <Box sx={{ fontWeight: 600 }}>
                                                        <FormattedMessage id="app.bonusSettlements.supplierDetail.noteToCustomerToolTipTitle" />
                                                    </Box>
                                                    <Box>{bonusSettlement?.noteToCustomer}</Box>
                                                </Stack>
                                            }
                                        >
                                            <CommentNoteIcon />
                                        </PoolTooltip>
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell sx={{ width: 140, textAlign: 'right' }}>
                                <Stack direction="row" justifyContent="flex-end" gap={1}>
                                    <strong>
                                        <FormattedNumber
                                            value={bonusSettlement?.memberBonus}
                                            currency={defaultCurrency}
                                            style="currency"
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                        />
                                    </strong>

                                    {calculationPayout && (
                                        <PoolTooltip
                                            title={
                                                <Stack>
                                                    <Box>
                                                        <PoolFormattedMessage id="app.bonusSettlements.supplierDetail.bonusMemberTooltip" />
                                                    </Box>
                                                    <FormattedMessage
                                                        id="app.bonusSettlements.supplierDetail.bonusMemberTooltipCalculation"
                                                        values={{
                                                            actual: intl.formatNumber(
                                                                bonusSettlement?.actualBonus ?? 0,
                                                                {
                                                                    maximumFractionDigits: 2,
                                                                    minimumFractionDigits: 2,
                                                                }
                                                            ),
                                                            memberBonus: intl.formatNumber(
                                                                bonusSettlement?.memberBonus ?? 0,
                                                                {
                                                                    maximumFractionDigits: 2,
                                                                    minimumFractionDigits: 2,
                                                                }
                                                            ),
                                                            currency: getCurrencySymbol(defaultCurrency, locale),
                                                            payout: calculationPayout,
                                                        }}
                                                    />
                                                </Stack>
                                            }
                                            variant="light"
                                        >
                                            <InformationOutlinedIcon htmlColor={theme.palette.grey[700]} />
                                        </PoolTooltip>
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell sx={{ width: 110, textAlign: 'center' }}>
                                <PoolTooltip
                                    title={
                                        <FormattedMessage
                                            id={
                                                bonusSettlement?.isFinished
                                                    ? 'app.bonusSettlements.supplierDetail.done'
                                                    : 'app.bonusSettlements.supplierDetail.open'
                                            }
                                        />
                                    }
                                >
                                    <AcceptToggle
                                        size="small"
                                        checked={bonusSettlement?.isFinished}
                                        onClick={() =>
                                            formikContext.setFieldValue(
                                                `customerBonusSettlements.${index}.isFinished`,
                                                !customerBonusSettlement.isFinished
                                            )
                                        }
                                        disabled={bonusSettlementSupplierDetail.readOnly}
                                    >
                                        <CheckIcon sx={{ fontSize: 20, color: theme => theme.palette.grey[500] }} />
                                    </AcceptToggle>
                                </PoolTooltip>
                            </TableCell>
                            {!bonusSettlementSupplierDetail.readOnly && (
                                <TableCell sx={{ p: 0 }}>
                                    <BonusSettlementSupplierDetailTableActionMenu
                                        customer={bonusSettlement}
                                        onEditCustomer={() =>
                                            handleEditCustomer(
                                                index,
                                                formikContext.values.customerBonusSettlements[index]
                                            )
                                        }
                                        onReclaimCustomer={() => handleReclaimCustomer(bonusSettlement)}
                                        onRevertReclaimCustomer={() => handleRevertReclaimCustomer(bonusSettlement)}
                                        onDeleteCustomer={() => handleRemoveCustomer(bonusSettlement)}
                                    />
                                </TableCell>
                            )}
                        </Fragment>
                    );
                }}
                fixedFooterContent={() => (
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.total" />
                        </TableCell>
                        {bonusConditions.map((condition: BonusCustomerCondition) => {
                            let value = 0;
                            let totalRevenue = 0;

                            for (const settlement of formikContext.values.customerBonusSettlements) {
                                const conditionValue = settlement.bonusConditionValues.find(
                                    bcv => bcv.bonusConditionId === condition.bonusCondition.id
                                );

                                if (conditionValue) {
                                    value += conditionValue.value ?? 0;
                                    totalRevenue += conditionValue.revenue ?? 0;
                                }
                            }

                            return (
                                <BonusConditionTableCellFooter
                                    key={condition.bonusCondition.id}
                                    bonusCondition={condition.bonusCondition}
                                    revenue={totalRevenue}
                                    defaultCurrency={defaultCurrency}
                                    value={value}
                                    locale={locale}
                                />
                            );
                        })}
                        <TableCell sx={{ width: 130, textAlign: 'right' }}>
                            <FormattedNumber
                                value={formikContext.values.customerBonusSettlements.reduce(
                                    (sum, settlement) => sum + (settlement.calculatedBonus || 0),
                                    0
                                )}
                                currency={defaultCurrency}
                                style="currency"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </TableCell>
                        <TableCell sx={{ width: 140, textAlign: 'right' }}>
                            <FormattedNumber
                                value={formikContext.values.customerBonusSettlements.reduce(
                                    (sum, settlement) => sum + (settlement.actualBonus || 0),
                                    0
                                )}
                                currency={defaultCurrency}
                                style="currency"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </TableCell>
                        <TableCell sx={{ width: 140, textAlign: 'right' }}>
                            <FormattedNumber
                                value={formikContext.values.customerBonusSettlements.reduce(
                                    (sum, settlement) => sum + (settlement.memberBonus || 0),
                                    0
                                )}
                                currency={defaultCurrency}
                                style="currency"
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </TableCell>
                        <TableCell sx={{ width: 110, textAlign: 'center' }}>
                            <FormattedMessage
                                id="app.bonusSettlements.supplierDetail.totalFinished"
                                values={{
                                    finished: formikContext.values.customerBonusSettlements.filter(
                                        settlement => settlement.isFinished
                                    ).length,
                                    total: formikContext.values.customerBonusSettlements.length,
                                }}
                            />
                        </TableCell>
                        {!bonusSettlementSupplierDetail.readOnly && <TableCell sx={{ p: 0 }} />}
                    </TableRow>
                )}
            />

            <BonusSettlementSupplierDetailEditModal
                open={openEditModal}
                settlementData={editModalData}
                onClose={() => {
                    setOpenEditModal(false);
                    setEditModalData(null);
                }}
            />
        </Paper>
    );
};
